import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useSWR from "swr";
import ReactGA from "react-ga4";
import { END_POINTS } from "constants/APIConstant";
import { flattenPinnedIndicators, safeList } from "./utils";

const PUBLICATIONS_PARAMS = {
  pageSize: 2,
  currentPage: 1,
};

export function useWhatsNewData() {
  const dir = useSelector(({ theme }) => theme.direction);
  const [newMedia, setNewMedia] = useState(0);

  const surveyCensuse = useSWR({ url: END_POINTS.SURVEY_CENSUSE });
  const importantPortal = useSWR({ url: END_POINTS.IMPORTANT_PORTAL });
  const latestNewsFetching = useSWR({ url: END_POINTS.LATEST_NEWS });
  const publicationsFetching = useSWR({
    url: END_POINTS.PUBLICATIONS,
    params: PUBLICATIONS_PARAMS,
  });
  const newToolsFetching = useSWR({ url: END_POINTS.NEW_TOOLS });
  const latestPhoto = useSWR({ url: END_POINTS.LATEST_PHOTO });
  const videosList = useSWR({ url: END_POINTS.LATEST_VIDEO });
  const dataChart = useSWR({ url: END_POINTS.PINNED_INDICATORS });

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
    ReactGA.send({ hitType: "pageview", page: "/new", title: "The new Page" });
  }, []);

  const surveyCensuses = useMemo(
    () => safeList(surveyCensuse, { slice: 4 }),
    [surveyCensuse.data, surveyCensuse.error, surveyCensuse.isLoading],
  );

  const importantPortals = useMemo(
    () => safeList(importantPortal),
    [importantPortal.data, importantPortal.error, importantPortal.isLoading],
  );

  const latestNews = useMemo(
    () => safeList(latestNewsFetching, { minLength: 1, slice: 6 }),
    [
      latestNewsFetching.data,
      latestNewsFetching.error,
      latestNewsFetching.isLoading,
    ],
  );

  const publications = useMemo(
    () => safeList(publicationsFetching, { minLength: 1 }),
    [
      publicationsFetching.data,
      publicationsFetching.error,
      publicationsFetching.isLoading,
    ],
  );

  const newTools = useMemo(
    () => safeList(newToolsFetching, { minLength: 1, slice: 2 }),
    [newToolsFetching.data, newToolsFetching.error, newToolsFetching.isLoading],
  );

  const photos = useMemo(
    () => safeList(latestPhoto),
    [latestPhoto.data, latestPhoto.error, latestPhoto.isLoading],
  );

  const videos = useMemo(
    () => safeList(videosList),
    [videosList.data, videosList.error, videosList.isLoading],
  );

  const chartData = useMemo(
    () =>
      !dataChart?.error && !dataChart.isLoading
        ? flattenPinnedIndicators(dataChart?.data?.data)
        : [],
    [dataChart?.data, dataChart?.error, dataChart?.isLoading],
  );

  const isLoading =
    surveyCensuse.isLoading ||
    latestPhoto.isLoading ||
    importantPortal.isLoading ||
    latestNewsFetching.isLoading ||
    publicationsFetching.isLoading ||
    newToolsFetching.isLoading ||
    dataChart.isLoading;

  return {
    dir,
    newMedia,
    setNewMedia,
    isLoading,
    surveyCensuses,
    importantPortals,
    latestNews,
    publications,
    newTools,
    photos,
    videos,
    chartData,
  };
}
